import { useRef, useState } from "react";
import { Camera, Upload, Loader2, ScanText, Save, X, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const DOC_TYPES = [
  { value: "carta_identita", label: "Carta Identità" },
  { value: "patente", label: "Patente" },
  { value: "codice_fiscale", label: "Codice Fiscale" },
  { value: "passaporto", label: "Passaporto" },
  { value: "altro", label: "Altro" },
];

/**
 * DocumentCapture — Componente riusabile per scattare foto documento,
 * leggere automaticamente i dati con OCR (GPT-4o vision) e salvarli come PDF.
 *
 * Props:
 *   - customerId: string  → se presente, salva il documento sul cliente
 *   - onOcrResult: (extracted) => {}  → callback dati estratti per auto-fill form
 *   - onClose: () => {}
 */
export default function DocumentCapture({ customerId, onOcrResult, onClose }) {
  const fileInputRef = useRef(null);
  const [docType, setDocType] = useState("carta_identita");
  const [docName, setDocName] = useState("");
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("File troppo grande (max 8 MB)");
    const reader = new FileReader();
    reader.onload = (e) => setImageDataUrl(e.target.result);
    reader.readAsDataURL(file);
  };

  const runOcr = async () => {
    if (!imageDataUrl) return toast.error("Seleziona o scatta una foto prima");
    setOcrLoading(true);
    try {
      const r = await api.post("/ocr/document", {
        image_base64: imageDataUrl,
        document_type: docType,
      });
      setExtracted(r.data.extracted);
      toast.success("Dati estratti dal documento");
      if (onOcrResult) onOcrResult(r.data.extracted);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore OCR");
    } finally {
      setOcrLoading(false);
    }
  };

  const saveDocument = async () => {
    if (!customerId) return toast.error("Nessun cliente selezionato");
    if (!imageDataUrl) return toast.error("Nessuna foto");
    setSaving(true);
    try {
      const r = await api.post(`/customers/${customerId}/documents/upload`, {
        name: docName || DOC_TYPES.find((t) => t.value === docType)?.label || "Documento",
        type: docType,
        image_base64: imageDataUrl,
      });
      toast.success(`Documento salvato (PDF, ${(r.data.size_bytes / 1024).toFixed(0)} KB)`);
      if (onClose) onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-[#0a0a0a] border border-white/10 rounded-3xl overflow-hidden max-h-[94vh] flex flex-col shadow-[0_0_120px_-20px_#ff0033]"
        data-testid="doc-capture">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-gradient-to-r from-[#ff0033]/[0.08] via-transparent to-transparent">
          <div>
            <div className="font-display text-lg font-bold inline-flex items-center gap-2">
              <Camera size={16} className="text-[#ff5577]" /> Scatta o carica documento
            </div>
            <div className="text-[10px] text-white/40 mt-0.5">OCR automatico via AI · salvato come PDF nel database</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-4 overflow-auto">
          {/* Tipo documento */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {DOC_TYPES.map((t) => (
              <button key={t.value} onClick={() => setDocType(t.value)}
                data-testid={`doctype-${t.value}`}
                className={`px-3 py-2 rounded-lg border text-xs uppercase tracking-wider transition-all ${docType === t.value ? "border-[#ff0033] bg-[#ff0033]/10 text-white" : "border-white/10 bg-white/[0.02] text-white/55 hover:border-white/25"}`}>
                {t.label}
              </button>
            ))}
          </div>

          <input type="text" value={docName} onChange={(e) => setDocName(e.target.value)} placeholder="Nome documento (es. CI fronte)"
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />

          {/* Capture */}
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => fileInputRef.current?.click()} data-testid="capture-btn"
              className="p-6 rounded-2xl bg-[#ff0033]/[0.06] border border-[#ff0033]/30 hover:bg-[#ff0033]/[0.10] text-center transition-all">
              <Camera size={28} className="mx-auto text-[#ff5577]" />
              <div className="text-sm font-bold mt-2">Scatta foto</div>
              <div className="text-[10px] text-white/45 mt-1">Tablet / Smartphone</div>
            </button>
            <button onClick={() => { fileInputRef.current.removeAttribute("capture"); fileInputRef.current?.click(); }}
              data-testid="upload-btn"
              className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] text-center transition-all">
              <Upload size={28} className="mx-auto text-white/60" />
              <div className="text-sm font-bold mt-2">Carica file</div>
              <div className="text-[10px] text-white/45 mt-1">Da computer</div>
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }}
          />

          {/* Preview */}
          {imageDataUrl && (
            <div className="rounded-xl overflow-hidden border border-white/10 relative">
              <img src={imageDataUrl} alt="Documento" className="w-full max-h-64 object-contain bg-black" />
              <button onClick={() => { setImageDataUrl(null); setExtracted(null); }}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 hover:bg-[#ff0033]/30 text-white">
                <X size={14} />
              </button>
            </div>
          )}

          {/* OCR Button */}
          {imageDataUrl && !extracted && (
            <button onClick={runOcr} disabled={ocrLoading} data-testid="ocr-btn"
              className="w-full p-3 rounded-xl bg-violet-500/15 border border-violet-500/30 text-violet-300 hover:bg-violet-500/25 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {ocrLoading ? <><Loader2 size={14} className="animate-spin" /> Lettura AI in corso…</> : <><ScanText size={14} /> Leggi dati automaticamente (OCR AI)</>}
            </button>
          )}

          {/* Dati estratti */}
          {extracted && (
            <div className="rounded-xl bg-emerald-500/[0.06] border border-emerald-500/25 p-4 space-y-2" data-testid="ocr-result">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 inline-flex items-center gap-1.5">
                <CheckCircle2 size={11} /> Dati estratti dal documento — rivedi prima di salvare
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {Object.entries(extracted).filter(([k, v]) => v && k !== "raw").map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[9px] uppercase tracking-widest text-white/40">{k.replace(/_/g, " ")}</div>
                    <div className="font-mono text-white/85 truncate">{v}</div>
                  </div>
                ))}
              </div>
              {onOcrResult && (
                <button onClick={() => { onOcrResult(extracted); toast.success("Campi auto-compilati nel form"); onClose && onClose(); }}
                  data-testid="apply-ocr-btn"
                  className="mt-2 w-full px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-bold uppercase tracking-widest inline-flex items-center justify-center gap-2">
                  <CheckCircle2 size={12} /> Auto-compila form cliente
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {customerId && (
          <div className="flex justify-between gap-3 px-6 py-4 border-t border-white/5 bg-black/40">
            <button type="button" onClick={onClose} className="px-5 py-2 rounded-full text-sm bg-white/[0.04] border border-white/10 hover:bg-white/10">Annulla</button>
            <button onClick={saveDocument} disabled={!imageDataUrl || saving} data-testid="save-doc-btn"
              className="px-6 py-2 rounded-full text-sm font-bold bg-[#ff0033] hover:bg-[#ff2255] text-white inline-flex items-center gap-2 disabled:opacity-50">
              {saving ? <><Loader2 size={13} className="animate-spin" /> Salvataggio…</> : <><Save size={13} /> Salva PDF nel cliente</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
